import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Initials } from "@/components/job/Lifecycle";
import { Video, CalendarClock, ClipboardCheck, Gift, Wand2, MapPin } from "lucide-react";

const REC = { advance: ["Advance", "text-primary bg-primary/12"], hold: ["Hold", "text-amber-600 dark:text-amber-300 bg-amber-400/12"], decline: ["Decline", "text-rose-500 bg-rose-500/12"] };
export const fmtTime = (d) => new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
export const dayLabel = (d) => {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const diff = Math.round((x - t) / 864e5);
  return diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : diff === -1 ? "Yesterday" : x.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
};

export function InterviewList({ items, selected, onSelect, testId }) {
  let last = null;
  return (
    <div className="space-y-2.5" data-testid={testId}>
      {items.map((it) => {
        const day = dayLabel(it.when);
        const head = day !== last ? (last = day) : null;
        return (
          <div key={it.id}>
            {head && head !== "Today" && <div className="text-[13px] text-muted-foreground pt-3 pb-2">{head}</div>}
            <button onClick={() => onSelect(it.id)} aria-current={selected === it.id} data-testid={`interview-${it.id}`}
              className={cn("w-full text-left hs-card flex gap-4 px-4 py-3.5 transition-colors", selected === it.id ? "border-primary/50 bg-primary/[0.05]" : "hover:border-foreground/20")}>
              <span className="w-14 shrink-0"><span className="block hs-mono font-semibold">{fmtTime(it.when)}</span><span className="block text-[11px] text-muted-foreground">{it.duration_min} min</span></span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold truncate">{it.candidate_name}</span>
                <span className="block text-[13px] text-muted-foreground truncate">{it.job_title}{it.kit_name ? ` · ${it.kit_name}` : ""}</span>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">{it.mode === "onsite" || it.mode === "On-site" ? <MapPin className="h-3 w-3" /> : <Video className="h-3 w-3" />}{it.mode}{it.kit_id ? " · kit ready" : ""}{it.status === "completed" && it.debrief?.recommendation ? ` · ${REC[it.debrief.recommendation]?.[0] || it.debrief.recommendation}` : ""}</span>
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

const Tile = ({ label, value }) => <div className="hs-card px-4 py-3 min-w-0"><div className="text-[13px] text-muted-foreground">{label}</div><div className="text-xl font-display font-semibold truncate">{value ?? "—"}</div></div>;

export function InterviewDetail({ it, kits, onReschedule, onDebrief, onOffer, onSaveKit }) {
  const nav = useNavigate();
  const [cand, setCand] = useState(null);
  useEffect(() => { setCand(null); if (it.candidate_id) api.get(`/candidates/${it.candidate_id}`).then((r) => setCand(r.data)).catch(() => {}); }, [it.candidate_id]);
  const kit = kits.find((k) => k.id === it.kit_id);
  const end = new Date(Date.parse(it.when) + (it.duration_min || 0) * 6e4);
  const weakest = [...(cand?.competencies || [])].sort((a, b) => (a.score ?? 101) - (b.score ?? 101))[0];
  const probe = cand?.debrief?.improvements?.[0]?.point || weakest?.name;
  const done = it.status === "completed";
  return (
    <div className="space-y-5 hs-rise" data-testid="interview-detail">
      <div className="flex flex-wrap items-start gap-4">
        <Initials name={it.candidate_name} className="h-12 w-12 text-sm" />
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-display font-semibold truncate">{it.candidate_name}{it.kit_name ? ` · ${it.kit_name}` : ""}</h2>
          <p className="text-[13px] text-muted-foreground">{it.job_title} · {dayLabel(it.when)} {fmtTime(it.when)}–{fmtTime(end)}{it.interviewers?.length ? ` · with ${it.interviewers.join(", ")}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!done && <Button variant="outline" onClick={() => onReschedule(it)} className="gap-1.5" data-testid={`reschedule-btn-${it.id}`}><CalendarClock className="h-4 w-4" /> Reschedule</Button>}
          {!done && <Button variant="outline" onClick={() => onDebrief(it)} className="gap-1.5" data-testid={`debrief-btn-${it.id}`}><ClipboardCheck className="h-4 w-4" /> Scorecard</Button>}
          {!done && it.join_link && <a href={it.join_link} target="_blank" rel="noreferrer" data-testid={`join-${it.id}`} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"><Video className="h-4 w-4" /> Join call</a>}
          {done && it.debrief?.recommendation === "advance" && <Button onClick={() => onOffer(it)} className="gap-1.5" data-testid={`offer-btn-${it.id}`}><Gift className="h-4 w-4" /> Send offer</Button>}
          {done && <Button variant="outline" onClick={() => onSaveKit(it.id)} className="gap-1.5" data-testid={`save-kit-${it.id}`}><Wand2 className="h-4 w-4" /> Save as kit</Button>}
        </div>
      </div>
      {!done && it.join_link && <p className="text-xs text-muted-foreground -mt-2">{it.invite_sent ? "Invite emailed with the join link." : "Join link ready — the invite email was not sent."}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile label="Fit" value={cand?.overall_score} />
        <Tile label="Assessment" value={cand?.objective_score} />
        <Tile label="Probe area" value={probe} />
      </div>
      {done && it.debrief && (
        <section className="hs-card p-5 space-y-3" data-testid={`debrief-${it.id}`}>
          <div className="flex items-center justify-between"><h3 className="text-[15px] font-semibold">Scorecard</h3>{REC[it.debrief.recommendation] && <span className={cn("text-xs font-semibold px-2.5 py-1 rounded-full", REC[it.debrief.recommendation][1])}>{REC[it.debrief.recommendation][0]}</span>}</div>
          {it.debrief.ratings?.length > 0 && <div className="flex flex-wrap gap-2">{it.debrief.ratings.map((r) => <span key={r.competency} className="text-xs px-2.5 py-1 rounded-md bg-foreground/[0.06]">{r.competency}: {r.score}/5</span>)}</div>}
          {it.debrief.notes && <p className="text-sm text-muted-foreground">{it.debrief.notes}</p>}
          <p className="text-[11px] text-muted-foreground">by {it.debrief.reviewer}</p>
        </section>
      )}
      <section className="hs-card" data-testid="interview-kit">
        <header className="flex items-center justify-between px-5 py-4 border-b border-border"><h3 className="text-[15px] font-semibold">Interview kit</h3>{it.candidate_id && <button onClick={() => nav(`/recruiter/candidate/${it.candidate_id}`)} className="text-[13px] text-primary hover:underline">Open candidate</button>}</header>
        {kit ? (
          <div className="divide-y divide-border">
            {(kit.competencies || []).flatMap((c) => (c.questions || []).map((q, qi) => (
              <div key={`${c.name}-${qi}`} className="px-5 py-3.5 grid sm:grid-cols-[160px_minmax(0,1fr)] gap-x-5 gap-y-1">
                <span className="text-xs text-muted-foreground hs-mono pt-0.5 truncate">{c.name}</span>
                <span><span className="block text-sm font-medium">{q}</span><span className="block text-xs text-muted-foreground">Look for: evidence of {c.name.toLowerCase()}</span></span>
              </div>
            )))}
          </div>
        ) : <p className="px-5 py-6 text-sm text-muted-foreground">No kit attached to this interview. Build one under Kits, then attach it when you schedule.</p>}
      </section>
    </div>
  );
}
